import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole
} from '@floating-ui/react'
import {
  faChevronDown,
  faMagnifyingGlass
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { UseInfiniteQueryResult } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import clsx from 'clsx'
import {
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import type { InputInFormProps } from '~/components/Form/Input/input'
import {
  errorMessage,
  inputError,
  inputLabel,
  inputWrapper
} from '~/components/Form/Input/TextInput.css'
import { Loader } from '~/components/Global/Elements/Loader/Loader'
import { Tooltip } from '~/components/Global/Elements/Tooltip/Tooltip'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { useInfiniteList } from '~/hooks/useInfiniteList'
import { themeClass } from '~/styles/theme.css'
import { normalizeSearchString } from '~/utils/sanitize'
import {
  dropDownValueBox,
  dropDownValueBoxExtraSmall,
  openIcon,
  openIconGhost,
  openIconOpen,
  optionItem,
  optionItemNotSelectable,
  optionsList,
  optionsPortal,
  optionsWrapper,
  optionsWrapperInline,
  searchInput,
  searchInputWrapper,
  selectedValueText,
  valueBoxActive,
  valueBoxAsMenu,
  valueBoxDisabled,
  valueBoxGhost,
  valueBoxNarrow,
  valueBoxUpdating,
  wrapper
} from './Dropdown.css'
import { DropdownOption as DropdownOptionComponent } from './DropdownOption'
import { DropdownSelectedValues } from './DropdownSelectedValues'
import {
  valueBoxExtraSmall,
  valueBoxFullWidth,
  valueBoxPlaceholder,
  valueBoxSmall
} from './Input.css'

export type SubOption = {
  label: string
  value: string
}

export type DropdownOption = {
  label: string
  value: string
  element?: ReactElement
  children?: DropdownOption[]
  group?: boolean
  /** Estimated row height in px for virtualization; set where the option UI is defined. */
  estimateSize?: number
  /** Called when the option is picked; return false to reject the selection (nothing is added, the panel stays open). */
  onSelect?: () => boolean
}

type DropdownOptionWithParent = DropdownOption & {
  parent?: DropdownOption
}

export type SubOptionConfig = {
  [primaryOptionValue: string]: SubOption[]
}

export type SelectedSubOptions = {
  [primaryOptionValue: string]: string[]
}

export type BaseDropdownProps = InputInFormProps & {
  prefix?: ReactElement
  name?: string
  options?: DropdownOption[]
  optionsLoading?: boolean
  optionsInfiniteQuery?: UseInfiniteQueryResult<DropdownOption[] | undefined>
  isUpdating?: boolean
  onSearchChange?: (search: string | undefined) => void
  label?: string | ReactElement
  small?: boolean
  extraSmall?: boolean
  fullWidth?: boolean
  narrow?: boolean
  disabled?: boolean | string
  placeholder?: string | ReactElement
  searchPlaceholder?: string
  emptyLabel?: string
  emptyMessage?: string
  valueBoxClassName?: string
  wrapperClassName?: string
  ghost?: boolean
  asMenu?: boolean
  searchable?: boolean
  searchShouldFilterOptions?: boolean
  /** When searching, Enter picks the first option even without keyboard highlight. */
  enterSelectsFirstOption?: boolean
  /** Clear the search input after an option is picked. */
  clearSearchOnSelect?: boolean
  required?: boolean
  onValueBoxClick?: () => void
  /** Renders search + list in flow (no value box, no floating portal). */
  inlineList?: boolean
  /** When true, options with children render a right-side floating submenu on hover instead of inline expand/collapse. */
  childrenAsSubmenu?: boolean
  // Sub-options configuration
  globalSubOptions?: SubOption[]
  subOptions?: SubOptionConfig
  allowMultipleSubOptions?: boolean
}

export type SingleDropdownProps = BaseDropdownProps & {
  multiple?: false
  /** @deprecated use defaultValueOption instead */
  defaultValue?: string | null
  defaultValueOption?: DropdownOption
  defaultValues?: never
  defaultValueOptions?: never
  onChange?: (value: string) => void
}

export type MultiDropdownProps = BaseDropdownProps & {
  multiple: true
  defaultValue?: never
  defaultValueOption?: never
  /** @deprecated use defaultValueOptions instead */
  defaultValues?: string[] | null
  defaultValueOptions?: DropdownOption[]
  onChange?: (value: string[]) => void
}

export type DropdownProps = SingleDropdownProps | MultiDropdownProps

export const Dropdown = ({
  name,
  prefix,
  options,
  optionsLoading,
  optionsInfiniteQuery,
  onSearchChange,
  defaultValue,
  defaultValues,
  defaultValueOption,
  defaultValueOptions,
  label,
  onChange,
  onValueBoxClick,
  small = false,
  extraSmall = false,
  fullWidth = false,
  narrow = false,
  disabled = false,
  placeholder,
  searchPlaceholder = 'Rechercher',
  emptyLabel,
  emptyMessage = 'Aucune option disponible',
  valueBoxClassName,
  wrapperClassName,
  ghost = false,
  register,
  watch,
  setValue,
  error,
  multiple = false,
  searchable = false,
  required = false,
  asMenu = false,
  isUpdating = false,
  globalSubOptions,
  subOptions,
  allowMultipleSubOptions = false,
  searchShouldFilterOptions = true,
  enterSelectsFirstOption = false,
  clearSearchOnSelect = false,
  inlineList = false,
  childrenAsSubmenu = false
}: DropdownProps): ReactElement => {
  if (!options && !optionsInfiniteQuery) {
    throw new Error('options or optionsInfiniteQuery is required')
  }
  const getValuesFromDefaultValues = useCallback(() => {
    if (multiple) {
      return defaultValues || defaultValueOptions?.map((opt) => opt.value) || []
    }
    return defaultValue !== undefined && defaultValue !== null
      ? [defaultValue]
      : defaultValueOption?.value
        ? [defaultValueOption.value]
        : []
  }, [
    defaultValue,
    defaultValues,
    multiple,
    defaultValueOptions,
    defaultValueOption
  ])

  const finalOptions = useMemo(() => {
    const base = options
      ? options
      : optionsInfiniteQuery
        ? (optionsInfiniteQuery.data ?? [])
        : []
    return emptyLabel
      ? [
          {
            label: emptyLabel,
            value: ''
          },
          ...base
        ]
      : base
  }, [options, optionsInfiniteQuery, emptyLabel])

  const finalFlatOptions = useMemo(() => {
    const flatOptions: (DropdownOption & { parent?: DropdownOption })[] = []
    const flatOption = (option: DropdownOption, parent?: DropdownOption) => {
      if (!option.group) {
        flatOptions.push({ ...option, parent })
      }
      if (option.children) {
        option.children.forEach((child) => {
          flatOption(child, option)
        })
      }
    }
    finalOptions.forEach((option) => {
      flatOption(option)
    })
    return flatOptions
  }, [finalOptions])

  const optionMatchesSearch = useCallback(
    (option: DropdownOption, term: string): boolean => {
      if (term.trim() === '') return true
      const match = normalizeSearchString(option.label).includes(
        normalizeSearchString(term)
      )
      if (match) return true
      if (option.children) {
        return option.children.some((child) => optionMatchesSearch(child, term))
      }
      return false
    },
    []
  )

  // visible options filtered by current search
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState<string | undefined>(undefined)
  // Index of the keyboard-highlighted option (-1 = none)
  const [activeIndex, setActiveIndex] = useState(-1)
  const visibleOptions = useMemo(() => {
    const list =
      search === undefined
        ? finalOptions
        : searchShouldFilterOptions
          ? finalOptions.filter(
              (opt) => opt.group || optionMatchesSearch(opt, search || '')
            )
          : finalOptions
    // Always keep the empty option if provided
    if (emptyLabel && (list.length === 0 || list[0]?.value !== '')) {
      return [
        {
          label: emptyLabel,
          value: ''
        },
        ...list
      ]
    }
    return list
  }, [
    finalOptions,
    search,
    optionMatchesSearch,
    emptyLabel,
    searchShouldFilterOptions
  ])

  const buildParentLabelPath = (option: DropdownOptionWithParent): string => {
    const getParentLabel = (option: DropdownOptionWithParent): string => {
      const flatOption = finalFlatOptions.find(
        (opt) => opt.value === option.value
      )
      if (flatOption?.parent) {
        return `${getParentLabel(flatOption.parent)} > ${flatOption.label}`
      }
      return option.label
    }
    return getParentLabel(option)
  }

  const [selectedValues, setSelectedValues] = useState<string[]>(
    getValuesFromDefaultValues()
  )
  const labelRef = useRef<HTMLLabelElement | null>(null)
  const searchRef = useRef<HTMLInputElement | null>(null)

  const [optionsSelectedStore, setOptionsSelectedStore] = useState<
    DropdownOptionWithParent[]
  >([])
  const [selectedSubOptions, setSelectedSubOptions] =
    useState<SelectedSubOptions>({})
  // Keep track of the widest popup width to avoid shrinking after widening
  const maxFloatingWidthRef = useRef<number | null>(null)

  // Virtualization using TanStack React Virtual
  const listRef = useRef<HTMLDivElement | null>(null)
  const rowVirtualizer = useVirtualizer({
    count: visibleOptions.length,
    getScrollElement: () => listRef.current,
    getItemKey: (index) => visibleOptions[index]?.value || index,
    estimateSize: (index) => {
      const option = visibleOptions[index]
      if (!option) return 40
      if (option.estimateSize !== undefined) return option.estimateSize
      if (option.group) return 36
      return 40
    },
    measureElement: (el) => (el as HTMLElement).offsetHeight,
    overscan: 8
  })

  const endOfListReached = useCallback(() => {
    if (
      optionsInfiniteQuery?.hasNextPage &&
      !optionsInfiniteQuery.isFetchingNextPage
    ) {
      optionsInfiniteQuery.fetchNextPage()
    }
  }, [optionsInfiniteQuery])

  // Use to trigger the infinite query when the last item is visible
  const { lastItemRef } = useInfiniteList({
    onEndOfListReached: endOfListReached,
    enabled: optionsInfiniteQuery !== undefined
  })

  // Keep track of the passed query options to display them in valuebox
  // even if options have changed (ex: dropdown is open and value is not in the new options)
  useEffect(() => {
    setOptionsSelectedStore((current) => {
      const currentValues = new Set(current.map((o) => o.value))
      const toAdd = finalFlatOptions.filter(
        (opt) =>
          !currentValues.has(opt.value) && selectedValues.includes(opt.value)
      )
      if (toAdd.length === 0) {
        return current
      }
      return [...current, ...toAdd]
    })
  }, [finalFlatOptions, selectedValues])

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    open: inlineList ? false : isOpen,
    onOpenChange: (open) => {
      // If the dropdown is open, set empty search leads to fetch all options
      if (search === undefined) {
        setSearch('')
      }
      if (isUpdating || disabled !== false) {
        return
      }
      setIsOpen(open)
    },
    middleware: [
      offset(5),
      flip(),
      shift(),
      size({
        apply({ rects, availableHeight, elements }) {
          const referenceWidth = rects.reference.width
          // Prefer scrollWidth to capture content-driven width when available
          const contentWidth =
            (elements.floating as HTMLElement).scrollWidth || 0
          const currentWidth = Math.max(referenceWidth, contentWidth)
          if (
            maxFloatingWidthRef.current === null ||
            currentWidth > maxFloatingWidthRef.current
          ) {
            maxFloatingWidthRef.current = currentWidth
          }
          Object.assign((elements.floating as HTMLElement).style, {
            minWidth: `${maxFloatingWidthRef.current ?? referenceWidth}px`,
            maxHeight: `${Math.max(0, availableHeight)}px`
          })
        }
      })
    ],
    whileElementsMounted: autoUpdate
  })

  const { isMounted, styles: transitionStyles } = useFloatingTransition(context)

  const dismiss = useDismiss(context, {
    enabled: !inlineList,
    outsidePress: (event) => {
      if (labelRef.current?.contains(event.target as Node)) {
        return false
      }
      return true
    }
  })
  const role = useRole(context, { role: 'listbox' })

  // Merge props into a single object for convenience
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { enabled: !inlineList }),
    dismiss,
    role
  ])

  if (register !== undefined && name !== undefined) {
    register(name)
    register(`${name}_suboption`)
  }

  useEffect(() => {
    if (onSearchChange) {
      onSearchChange(search)
    }
  }, [search, onSearchChange])

  useEffect(() => {
    if (name !== undefined && watch?.(name) !== undefined) {
      // If the value is already set, don't set default values
      return
    }
    setSelectedValues(getValuesFromDefaultValues())
    if (setValue && name) {
      setValue(
        name,
        multiple
          ? getValuesFromDefaultValues()
          : getValuesFromDefaultValues()[0]
      )
    }
  }, [getValuesFromDefaultValues, setValue, name, multiple, watch])

  // Sync selectedValues with form state changes
  const watchedValue = watch?.(name ?? '')
  useEffect(() => {
    if (name && watchedValue !== undefined) {
      const newSelectedValues = multiple
        ? Array.isArray(watchedValue)
          ? watchedValue
          : []
        : watchedValue
          ? [String(watchedValue)]
          : []
      if (newSelectedValues.join(',') !== selectedValues.join(',')) {
        setSelectedValues(newSelectedValues)
      }
    }
  }, [name, watchedValue, multiple, selectedValues])

  // Sync selectedSubOptions with form state changes
  const watchedSubOptionValue = watch?.(name ? `${name}_suboption` : '')
  useEffect(() => {
    if (!name) return

    if (watchedSubOptionValue !== undefined) {
      const newSelectedSubOptions =
        typeof watchedSubOptionValue === 'object' &&
        watchedSubOptionValue !== null &&
        !Array.isArray(watchedSubOptionValue)
          ? (watchedSubOptionValue as SelectedSubOptions)
          : {}

      setSelectedSubOptions(newSelectedSubOptions)
    } else if (Object.keys(selectedSubOptions).length > 0) {
      // Clear suboptions if form value is cleared
      setSelectedSubOptions({})
    }
  }, [name, watchedSubOptionValue, selectedSubOptions])

  const updateSelectedValues = (values: string[]) => {
    if (!asMenu) {
      setSelectedValues(values)

      if (setValue && name) {
        setValue(name, multiple ? values : (values[0] ?? ''), {
          shouldValidate: true,
          shouldDirty: true
        })

        // Also update sub-options if they exist
        if ((globalSubOptions || subOptions) && name) {
          setValue(`${name}_suboption`, selectedSubOptions, {
            shouldValidate: true,
            shouldDirty: true
          })
        }
      }
    }

    if (onChange && multiple) {
      ;(onChange as (value: string[]) => void)(values)
    } else if (onChange && !multiple) {
      ;(onChange as (value: string) => void)(values[0] ?? '')
    }
  }

  const getSubOptionsForValue = (value: string): SubOption[] => {
    if (globalSubOptions) {
      return globalSubOptions
    }
    if (subOptions?.[value]) {
      return subOptions[value]
    }
    return []
  }

  const toggleSubOption = (primaryValue: string, subOptionValue: string) => {
    setSelectedSubOptions((prev) => {
      const currentSubOptions = prev[primaryValue] || []
      let newSubOptions: string[]

      if (currentSubOptions.includes(subOptionValue)) {
        newSubOptions = currentSubOptions.filter((v) => v !== subOptionValue)
      } else {
        if (allowMultipleSubOptions) {
          newSubOptions = [...currentSubOptions, subOptionValue]
        } else {
          newSubOptions = [subOptionValue]
        }
      }

      const updated = {
        ...prev,
        [primaryValue]: newSubOptions
      }

      if (newSubOptions.length === 0) {
        delete updated[primaryValue]
      }

      if (setValue && name) {
        setValue(`${name}_suboption`, updated, {
          shouldValidate: true,
          shouldDirty: true
        })
      }

      return updated
    })
  }

  const handleSelection = (option: DropdownOption): boolean => {
    if (option.onSelect?.() === false) {
      return false
    }

    let newValues: string[]
    if (!multiple && selectedValues.includes(option.value)) {
      if (option.value === '' && emptyLabel !== undefined && onChange) {
        ;(onChange as (value: string) => void)('')
      }
      return true
    }

    if (selectedValues.includes(option.value)) {
      // If the value is already selected, remove it
      newValues = selectedValues.filter((v) => v !== option.value)
    } else if (!multiple) {
      // If the value is not selected and it's a single selection, set the value
      newValues = [option.value]
    } else {
      // Otherwise, add it
      newValues = [...selectedValues, option.value]
    }

    updateSelectedValues(newValues)

    if (clearSearchOnSelect && (search ?? '') !== '') {
      setSearch('')
      onSearchChange?.('')
    }

    if (!multiple && !inlineList) {
      setIsOpen(false)
    }
    return true
  }

  const removeValue = (value: string) => {
    let newValues = selectedValues.filter((v) => v !== value)
    if (newValues.length === 0 && emptyLabel !== undefined) {
      newValues = ['']
    }

    setSelectedSubOptions((prev) => {
      const updated = { ...prev }
      delete updated[value]

      if (setValue && name) {
        setValue(`${name}_suboption`, updated, {
          shouldValidate: true,
          shouldDirty: true
        })
      }

      return updated
    })

    updateSelectedValues(newValues)
  }

  useEffect(() => {
    if (inlineList && search === undefined) {
      setSearch('')
    }
  }, [inlineList, search])

  useEffect(() => {
    if (searchRef.current && (isOpen || inlineList)) {
      searchRef.current.focus({ preventScroll: true })
    }
  }, [isOpen, inlineList])

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on search/open change
  useEffect(() => {
    setActiveIndex(-1)
  }, [search, isOpen])

  // Keep the keyboard-highlighted option scrolled into view
  useEffect(() => {
    if (activeIndex >= 0) {
      rowVirtualizer.scrollToIndex(activeIndex, { align: 'auto' })
    }
  }, [activeIndex, rowVirtualizer])

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (visibleOptions.length === 0) {
        return
      }
      setActiveIndex((index) => Math.min(index + 1, visibleOptions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (visibleOptions.length === 0) {
        return
      }
      setActiveIndex((index) => (index <= 0 ? -1 : index - 1))
    } else if (e.key === 'Enter') {
      // Prevent submitting the surrounding form when validating an option
      e.preventDefault()
      const fallbackOption =
        enterSelectsFirstOption && (search ?? '').trim() !== ''
          ? visibleOptions.find((opt) => !opt.group)
          : undefined
      const option =
        activeIndex >= 0 ? visibleOptions[activeIndex] : fallbackOption
      if (option && !option.group && handleSelection(option)) {
        setIsOpen(false)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const optionsPanelInner = (
    <>
      {(onSearchChange || searchable) && (
        <div className={searchInputWrapper}>
          <FontAwesomeIcon icon={faMagnifyingGlass} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            ref={searchRef}
            className={searchInput}
            placeholder={searchPlaceholder}
          />
        </div>
      )}
      <div className={optionsList} ref={listRef}>
        <div
          style={{
            height: rowVirtualizer.getTotalSize() || undefined
          }}
        >
          <div
            style={{
              transform: `translateY(${rowVirtualizer.getVirtualItems()[0]?.start ?? 0}px)`
            }}
          >
            {visibleOptions.length === 0 &&
            !optionsInfiniteQuery?.isFetching &&
            !optionsLoading ? (
              <div className={clsx(optionItem, optionItemNotSelectable)}>
                {emptyMessage}
              </div>
            ) : (
              rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const option = visibleOptions[virtualRow.index]
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                  >
                    {option && (
                      <DropdownOptionComponent
                        option={option}
                        selectedValues={selectedValues}
                        multiple={multiple}
                        asMenu={asMenu}
                        isUpdating={isUpdating}
                        disabled={disabled}
                        onSelection={handleSelection}
                        childrenAsSubmenu={childrenAsSubmenu}
                        searchTerm={search}
                        searchTermShouldFilter={searchShouldFilterOptions}
                        isActive={virtualRow.index === activeIndex}
                      />
                    )}
                  </div>
                )
              })
            )}
            {(optionsInfiniteQuery?.isFetching || optionsLoading) && (
              <Loader small={true} forceText={true} />
            )}
            {optionsInfiniteQuery &&
              finalOptions.length > 0 &&
              optionsInfiniteQuery.hasNextPage && <div ref={lastItemRef} />}
          </div>
        </div>
      </div>
    </>
  )

  const DropdownEl = (
    <>
      <Tooltip
        text={typeof disabled === 'string' ? disabled : undefined}
        asChild
      >
        <div
          className={clsx(wrapper, wrapperClassName)}
          ref={inlineList ? refs.setReference : undefined}
        >
          {!inlineList && (
            <div
              ref={refs.setReference}
              className={clsx(
                dropDownValueBox,
                isUpdating ? valueBoxUpdating : null,
                isOpen ? valueBoxActive : null,
                selectedValues.length === 0 && !asMenu
                  ? valueBoxPlaceholder
                  : null,
                small ? valueBoxSmall : null,
                extraSmall
                  ? [valueBoxExtraSmall, dropDownValueBoxExtraSmall]
                  : null,
                fullWidth ? valueBoxFullWidth : null,
                narrow ? valueBoxNarrow : null,
                asMenu ? valueBoxAsMenu : null,
                ghost ? valueBoxGhost : null,
                valueBoxClassName,
                error && inputError,
                disabled ? valueBoxDisabled : null
              )}
              {...getReferenceProps()}
            >
              {prefix}
              {optionsInfiniteQuery?.isFetching ? (
                <span className={selectedValueText}>Chargement...</span>
              ) : (
                <DropdownSelectedValues
                  selectedValues={selectedValues}
                  optionsSelectedStore={optionsSelectedStore}
                  multiple={multiple}
                  required={required}
                  emptyLabel={emptyLabel}
                  placeholder={placeholder}
                  isUpdating={isUpdating}
                  disabled={disabled}
                  selectedSubOptions={selectedSubOptions}
                  globalSubOptions={globalSubOptions}
                  subOptions={subOptions}
                  allowMultipleSubOptions={allowMultipleSubOptions}
                  onValueBoxClick={onValueBoxClick}
                  onRemoveValue={removeValue}
                  onToggleSubOption={toggleSubOption}
                  buildParentLabelPath={buildParentLabelPath}
                  getSubOptionsForValue={getSubOptionsForValue}
                  setIsOpen={setIsOpen}
                />
              )}
              <FontAwesomeIcon
                icon={faChevronDown}
                className={clsx(
                  openIcon,
                  isOpen ? openIconOpen : null,
                  ghost ? openIconGhost : null
                )}
              />
            </div>
          )}
          {inlineList ? (
            <div
              className={clsx(themeClass, optionsWrapper, optionsWrapperInline)}
            >
              {optionsPanelInner}
            </div>
          ) : (
            <FloatingPortal>
              {isMounted && (
                <div
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className={optionsPortal}
                  {...getFloatingProps()}
                >
                  <div
                    style={{ ...transitionStyles }}
                    className={clsx(themeClass, optionsWrapper)}
                  >
                    {optionsPanelInner}
                  </div>
                </div>
              )}
            </FloatingPortal>
          )}
        </div>
      </Tooltip>
      {error && (
        <div className={errorMessage} role="alert">
          {Array.isArray(error) ? error[0].message : error.message}
        </div>
      )}
    </>
  )

  if (!label) {
    return DropdownEl
  }
  return (
    <div className={inputWrapper}>
      <label
        htmlFor={name}
        className={inputLabel}
        ref={labelRef}
        onClick={(e) => {
          if (isUpdating || disabled !== false) {
            e.stopPropagation()
            return
          }
          setIsOpen(true)
        }}
      >
        {label}
      </label>
      {DropdownEl}
    </div>
  )
}
